import type { SxProps, Theme } from "@mui/material/styles";

/**
 * Shared flex layout for the SKU detail main column: fill height, allow children
 * with `overflow: auto` / `minHeight: 0` to scroll correctly.
 */
const flexColumnFillerSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  width: 1,
};

const flexColumnFillerClippedSx: SxProps<Theme> = {
  ...flexColumnFillerSx,
  overflow: "hidden",
};

/** PDP list / reviews list / similar: only this region scrolls. */
export const skuStoreScrollableBodySx: SxProps<Theme> = {
  flex: 1,
  minHeight: 0,
  overflow: "auto",
  width: 1,
};

/** Simple subpages (variants, media) where the whole page body scrolls. */
export const skuStoreSubpageScrollableRootSx: SxProps<Theme> = {
  ...skuStoreScrollableBodySx,
  minWidth: 0,
  pb: 5,
};

/** Product detail + reviews: fixed header, scrollable content below. */
export const skuStoreSplitViewShellSx: SxProps<Theme> =
  flexColumnFillerClippedSx;

export const skuStoreSplitViewHeaderSlotSx: SxProps<Theme> = {
  flexShrink: 0,
  width: 1,
  minWidth: 0,
};

export const skuStoreSplitViewListRegionSx: SxProps<Theme> = {
  ...skuStoreScrollableBodySx,
  pt: 3,
  px: { xs: 0, sm: 6 },
  pb: 5,
  borderTop: 1,
  borderColor: "divider",
};

/** Product Details page: `AppForm` must stretch as a flex child. */
export const skuStorePdpFormPageRootSx: SxProps<Theme> = {
  ...flexColumnFillerClippedSx,
  "& > *:first-of-type": {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    width: 1,
  },
};

/** PDP form: tabs row + scrollable sections. */
export const skuStorePdpFormRootSx: SxProps<Theme> = flexColumnFillerClippedSx;

/**
 * CSS grid / flex field layouts: horizontal gap (Figma #5 / 16px) and vertical
 * gap between rows (Figma #6 / 24px).
 */
export const skuStorePdpFormFieldGridGapSx: SxProps<Theme> = {
  columnGap: 5,
  rowGap: 7,
};

/**
 * Vertical gap between consecutive field blocks in PDP (stacked grids, multilines, etc.) — 24px,
 * matches {@link skuStorePdpFormFieldGridGapSx} `rowGap`.
 */
export const skuStorePdpFormFieldRowGroupStackGapSx: SxProps<Theme> = {
  gap: 7,
};
